import React, { FC, useState, useEffect } from 'react';
import _ from 'lodash';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { updateQueryParams } from '@utils/route';
import { QueryParams } from '@common/types';
import ControlledCheckboxGroup from './ControlledCheckboxGroup';
import {
  getMultiFilterSelectionsFromIds,
  getQueryParamsFromMultiSelection,
  getSelectedValues,
} from './multiSelectionFilterUtils';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { useFiltersShouldClearSearch } from '@components/Hooks/useFiltersShouldClearSearch';

type Props = {
  selectedValue: string[];
  items: Record<string, string>;
  langTokenPrefix: string;
  updatedQueryParam: keyof QueryParams;
  dataHook?: string;
  type: string;
};

export type MultiFilterSelections = Array<{
  id: string;
  label: string;
  isChecked: boolean;
  showInfoIcon: boolean;
}>;

const MultiSelectionFilter: FC<Props> = ({
  selectedValue,
  items,
  langTokenPrefix,
  updatedQueryParam,
  dataHook,
  type,
}) => {
  const [selections, setSelections] = useState<MultiFilterSelections>([]);
  const { navigate, router } = useShallowRouter();
  const { logBiClick } = useTemplatesBi({ is_mobile: false });
  const shouldClearSearch = useFiltersShouldClearSearch();

  useEffect(() => {
    setSelections(
      getMultiFilterSelectionsFromIds({
        items,
        selectionIds: selectedValue,
        showInfoIcons: true,
      }),
    );
  }, [items, selectedValue]);

  const onChangeSelection = (eventData: { id: string; checked: boolean }) => {
    const selectedValues = getSelectedValues(selections, eventData);

    logBiClick({
      click_type: `${eventData.checked ? 'mark' : 'unmark'} filter`,
      click_data: { filter_name: `${type}-wix ${eventData.id}` },
    });

    navigate(
      updateQueryParams(
        getQueryParamsFromMultiSelection(updatedQueryParam, selectedValues),
        router,
        { search: shouldClearSearch },
      ),
    );
    setSelections(selectedValues);
  };

  return (
    <ControlledCheckboxGroup
      selections={selections}
      onChange={onChangeSelection}
      langTokenPrefix={langTokenPrefix}
      textSize="small"
      dataHook={dataHook}
    />
  );
};

export default MultiSelectionFilter;
